import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import {
  CheckCircle2, Copy, ExternalLink, Mail, Phone, Calendar, KeyRound,
  ShieldCheck, ArrowRight, ClipboardCheck, Home,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const fmtDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const StepPill = ({ label, done, icon: Icon }) => (
  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border ${
    done ? "border-lime-400/40 bg-lime-400/10 text-lime-300"
         : "border-white/10 bg-white/[0.03] text-white/50"}`}>
    <Icon size={12} /> {label}
  </div>
);

/**
 * Customer confirmation page — shown after signing / paying.
 * Replaces the old "dumped back to homepage" experience.
 */
export default function BookingConfirmation() {
  const { bookingId } = useParams();
  const [params]      = useSearchParams();
  const navigate      = useNavigate();
  const shareToken    = params.get("token") || "";
  const paidFlag      = params.get("paid") === "1";
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // The public share endpoint returns the exact fields we need without
        // requiring authentication.
        const r = shareToken
          ? await api.get(`/public/bookings/${bookingId}?token=${encodeURIComponent(shareToken)}`)
          : await api.get(`/bookings/${bookingId}`);
        setBooking(r.data);
      } catch {
        // Fall back gracefully — never block the confirmation UX.
      } finally { setLoading(false); }
    })();
  }, [bookingId, shareToken]);

  const shareUrl = shareToken
    ? `${window.location.origin}/b/${bookingId}?token=${shareToken}`
    : "";
  const pending = booking?.status === "pending_approval";
  const paid    = paidFlag || ["confirmed", "in_progress", "completed"].includes(booking?.status);
  const firstName = (booking?.full_name || "").split(" ")[0] || "friend";

  return (
    <section
      data-testid="booking-confirmation"
      className="mx-auto max-w-3xl px-5 sm:px-8 py-10 sm:py-14"
    >
      <div className="glass rounded-3xl p-8 sm:p-12 text-center space-y-6 noise-overlay relative overflow-hidden">
        <div className="mx-auto w-16 h-16 rounded-full bg-cyan/15 flex items-center justify-center ring-1 ring-cyan/30">
          <CheckCircle2 size={36} className="text-cyan" />
        </div>
        <div>
          <div className="label-caps text-cyan text-[11px] mb-2">
            {pending ? "Contract signed · pending approval" : paid ? "Reservation confirmed" : "Reservation received"}
          </div>
          <h1 className="font-serif text-3xl sm:text-5xl leading-tight">
            Thanks, {firstName}.
          </h1>
          <p className="text-white/70 text-sm sm:text-base leading-relaxed max-w-xl mx-auto mt-3">
            {pending
              ? <>Your signed rental agreement for the <strong>{booking?.vehicle_name || "vehicle"}</strong> is
                 with our team for final approval. You'll get an email at
                 <span className="text-white"> {booking?.customer_email || "your inbox"}</span> once it's cleared —
                 usually within a few hours.</>
              : <>Your <strong>{booking?.vehicle_name || "rental"}</strong> is locked in. A confirmation and pickup
                 instructions are on their way to
                 <span className="text-white"> {booking?.customer_email || "your inbox"}</span>.</>}
          </p>
        </div>

        {/* Booking summary card */}
        {booking && (
          <div className="rounded-2xl bg-ink-950/60 border border-white/8 p-5 text-sm text-left max-w-md mx-auto">
            <div className="label-caps text-[10px] text-white/40 mb-3">Reservation details</div>
            <dl className="space-y-2">
              <div className="flex justify-between gap-4"><dt className="text-white/50">Confirmation #</dt>
                <dd className="text-gold font-mono truncate ml-2">
                  {booking.reservation_id || booking.id}
                </dd></div>
              {booking.start_at && (
                <div className="flex justify-between gap-4"><dt className="text-white/50">Pickup</dt>
                  <dd className="text-white">{fmtDT(booking.start_at)}</dd></div>
              )}
              {booking.end_at && (
                <div className="flex justify-between gap-4"><dt className="text-white/50">Return</dt>
                  <dd className="text-white">{fmtDT(booking.end_at)}</dd></div>
              )}
              {booking.pickup_location && (
                <div className="flex justify-between gap-4"><dt className="text-white/50">Location</dt>
                  <dd className="text-white truncate ml-2">{booking.pickup_location}</dd></div>
              )}
              {typeof booking.amount === "number" && (
                <div className="flex justify-between gap-4"><dt className="text-white/50">Total</dt>
                  <dd className="text-gold">${booking.amount.toFixed(2)}</dd></div>
              )}
              {typeof booking.deposit === "number" && booking.deposit > 0 && (
                <div className="flex justify-between gap-4"><dt className="text-white/50">Deposit</dt>
                  <dd>${booking.deposit.toFixed(2)}</dd></div>
              )}
            </dl>
          </div>
        )}

        {/* Status timeline */}
        <div className="flex flex-wrap gap-2 justify-center">
          <StepPill label="Contract signed"       done={true}                                icon={ClipboardCheck} />
          <StepPill label="Vehicle assigned"      done={!!booking?.vehicle_assignment?.vin} icon={KeyRound} />
          <StepPill label={paid ? "Payment received" : "Payment pending"} done={paid}       icon={ShieldCheck} />
          <StepPill label="Pickup ready"          done={booking?.status === "in_progress"} icon={Calendar} />
        </div>

        {/* Share link */}
        {shareUrl && (
          <div className="rounded-2xl border border-electric/25 bg-electric/5 p-4 max-w-lg mx-auto space-y-2">
            <div className="label-caps text-electric text-[10px]">Your reservation link</div>
            <div className="text-[11px] text-white/70 break-all font-mono">{shareUrl}</div>
            <div className="flex gap-2 justify-center pt-1">
              <button
                data-testid="confirmation-copy-link"
                onClick={() => {
                  navigator.clipboard?.writeText(shareUrl)
                    .then(() => toast.success("Link copied"))
                    .catch(() => {});
                }}
                className="px-4 py-2 rounded-full text-xs border border-white/15 text-white/80 hover:border-white/40 inline-flex items-center gap-1.5">
                <Copy size={12} /> Copy link
              </button>
              <button
                data-testid="confirmation-open-share"
                onClick={() => window.open(shareUrl, "_blank")}
                className="px-4 py-2 rounded-full text-xs bg-electric text-ink-900 font-semibold hover:brightness-110 inline-flex items-center gap-1.5">
                <ExternalLink size={12} /> Open reservation
              </button>
            </div>
            <p className="text-[10px] text-white/40">
              Save this link — you can revisit anytime to see status, contract, and inspection.
            </p>
          </div>
        )}

        {/* What's next */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-left max-w-2xl mx-auto">
          <div className="rounded-xl border border-white/8 bg-black/30 p-4">
            <Mail size={14} className="text-cyan mb-1.5" />
            <div className="text-white text-sm mb-0.5">Check your email</div>
            <div className="text-white/50 text-[11px]">
              Your signed contract and pickup instructions arrive shortly. Check spam if you don't see it.
            </div>
          </div>
          <div className="rounded-xl border border-white/8 bg-black/30 p-4">
            <Phone size={14} className="text-cyan mb-1.5" />
            <div className="text-white text-sm mb-0.5">Questions?</div>
            <div className="text-white/50 text-[11px]">
              Text or call our concierge at
              <a href="tel:+19298993467" className="text-cyan hover:underline"> +1 (929) 899-3467</a>.
            </div>
          </div>
          <div className="rounded-xl border border-white/8 bg-black/30 p-4">
            <Calendar size={14} className="text-cyan mb-1.5" />
            <div className="text-white text-sm mb-0.5">Day-of pickup</div>
            <div className="text-white/50 text-[11px]">
              Bring your license and the card used to book. We'll walk the vehicle with you before keys.
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 justify-center pt-2">
          <Link
            to="/bookings"
            data-testid="confirmation-my-bookings"
            className="btn-gold px-5 py-2.5 rounded-full text-xs inline-flex items-center gap-2">
            View my bookings <ArrowRight size={13} />
          </Link>
          <Link
            to="/fleet"
            data-testid="confirmation-back-to-fleet"
            className="px-5 py-2.5 rounded-full text-xs label-caps border border-white/12 hover:border-white/30">
            Back to fleet
          </Link>
          <button
            onClick={() => navigate("/")}
            data-testid="confirmation-home"
            className="px-5 py-2.5 rounded-full text-xs label-caps border border-white/8 text-white/50 hover:text-white/80 inline-flex items-center gap-1.5">
            <Home size={12} /> Home
          </button>
        </div>

        {loading && !booking && (
          <div className="text-white/40 text-xs">Loading your reservation details…</div>
        )}
      </div>
    </section>
  );
}
